'use client'

import { useNavigationGuard } from '@/components/navigation/navigation-guard-context'
import { cn } from '@/lib/utils'
import { ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Fragment, type ComponentProps } from 'react'

export interface BreadcrumbItem {
  label: string
  href?: string
  onClick?: () => void
}

export interface BreadcrumbProps extends ComponentProps<'nav'> {
  items: BreadcrumbItem[]
}

export function Breadcrumb({ className, items, ...props }: BreadcrumbProps) {
  const { requestNavigation } = useNavigationGuard()
  const pathname = usePathname()
  const router = useRouter()

  if (items.length === 0) return null

  return (
    <nav aria-label="Breadcrumb" className={cn('px-1 py-0.5', className)} {...props}>
      <ol className="flex flex-wrap items-center gap-2">
        {items.map((item, index) => {
          const isCurrentPage = index === items.length - 1
          const { href, onClick } = item

          return (
            <Fragment key={`${item.label}-${index}`}>
              <li data-slot="link" className="min-w-0 text-ui-body-b1 leading-ui-body-b1 font-medium">
                {href && !isCurrentPage ? (
                  <Link
                    href={href}
                    onNavigate={(event) => {
                      if (href === pathname) return
                      event.preventDefault()
                      requestNavigation(() => router.push(href))
                    }}
                    className="text-creation-secondary-container-on-surface-default hover:text-creation-primary-container-on-surface-hover hover:underline focus-visible:text-creation-primary-container-on-surface-focused focus-visible:underline focus-visible:outline-none"
                  >
                    {item.label}
                  </Link>
                ) : onClick && !isCurrentPage ? (
                  <button
                    type="button"
                    onClick={() => requestNavigation(onClick)}
                    className="text-creation-secondary-container-on-surface-default hover:text-creation-primary-container-on-surface-hover hover:underline focus-visible:text-creation-primary-container-on-surface-focused focus-visible:underline focus-visible:outline-none"
                  >
                    {item.label}
                  </button>
                ) : (
                  <span
                    aria-current={isCurrentPage ? 'page' : undefined}
                    className={cn(
                      isCurrentPage
                        ? 'text-creation-primary-container-on-surface-focused'
                        : 'text-creation-secondary-container-on-surface-default',
                    )}
                  >
                    {item.label}
                  </span>
                )}
              </li>
              {!isCurrentPage && (
                <li aria-hidden="true" className="size-4 shrink-0 text-main-icon-lv02">
                  <ChevronRight className="size-4" />
                </li>
              )}
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}
